import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, LogOutIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/lib/theme";
import { apiClient } from "../../lib/api";
import { authClient } from "../../lib/auth-client";
import { NotificationsSection } from "./components/NotificationsSection";
import { THEME_OPTIONS } from "./constants";
import { getTimezones } from "./utils";

export function SettingsPage() {
  const { mode, setMode } = useTheme();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiClient.getCurrentUser(),
  });

  useEffect(() => {
    if (me) {
      setName(me.name ?? "");
      setTimezone(me.timezone);
    }
  }, [me]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.patchCurrentUser({
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(timezone ? { timezone } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (event) => {
      setSaved(false);
      setError(event.message);
    },
  });

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isDirty = !!me && (name !== (me.name ?? "") || timezone !== me.timezone);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
      <header>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Preferences</p>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Profile</h2>
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="display-name">Display name</Label>
            <Input
              id="display-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="How your circle sees you"
            />
            <p className="text-xs text-muted-foreground">
              Shown to circle members in events and progress views.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="timezone">Timezone</Label>
            <select
              id="timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              className="w-full h-9 rounded-lg border border-input bg-transparent px-3 text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none dark:bg-input/30"
            >
              {getTimezones().map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replaceAll("_", " ")}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Goal due times and missed-goal notifications follow this timezone.
              {browserTimezone && timezone !== browserTimezone ? (
                <>
                  {" "}
                  Your device is on{" "}
                  <button
                    className="text-primary hover:underline"
                    onClick={() => setTimezone(browserTimezone)}
                  >
                    {browserTimezone.replaceAll("_", " ")}
                  </button>
                  .
                </>
              ) : null}
            </p>
          </div>

          {me ? (
            <p className="text-xs text-muted-foreground border-t border-border/60 pt-3">
              Signed in as <span className="font-medium text-foreground">{me.email}</span>
            </p>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving…" : "Save profile"}
            </Button>
            {saved ? (
              <span className="flex items-center gap-1 text-xs text-primary font-medium">
                <CheckIcon className="w-3.5 h-3.5" /> Saved
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <NotificationsSection />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Appearance</h2>
        <div className="space-y-2">
          {THEME_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = mode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setMode(opt.value)}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors text-left ${
                  isActive
                    ? "border-primary bg-accent text-accent-foreground"
                    : "border-border bg-card hover:bg-muted/50"
                }`}
              >
                <Icon
                  className={`w-5 h-5 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`}
                />
                <div className="flex-1">
                  <div className={`text-sm font-medium ${isActive ? "text-accent-foreground" : ""}`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{opt.description}</div>
                </div>
                {isActive ? <div className="w-2 h-2 rounded-full bg-primary shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Account</h2>
        <div className="rounded-xl border border-border bg-card px-4 py-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => void authClient.signOut().then(() => window.location.assign("/login"))}
          >
            <LogOutIcon className="w-4 h-4" />
            Sign out
          </Button>
        </div>
      </section>

      <section className="space-y-2 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Consistency Circle</span> — calm, privacy-aware habit tracking.
        </p>
        <p className="text-xs text-muted-foreground">
          Warm neutrals · teal-green accent · your data stays yours.
        </p>
      </section>
    </div>
  );
}
